import { useEffect, useState } from 'react';
import { useDropzone, Accept, FileWithPath } from 'react-dropzone';
import { useTranslation } from 'next-i18next';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import { Button, filesIcons } from '@forma/forma-ui-kit';

import styles from './file-input.module.css';

interface FileInputProps {
  accept?: Accept,
  onChange: (file: File[]) => void,
  className?: string,
  maxFiles?: number,
  buttonText?: string,
}

const FileInput = ({
  accept, onChange, className, maxFiles = 1, buttonText
}: FileInputProps) => {
  const { t } = useTranslation();
  const [isLoad, setIsLoad] = useState<boolean>(false);
  const [files, setFiles] = useState<FileWithPath[]>([]);

  const { getRootProps, getInputProps, open } = useDropzone({
    maxFiles: 1,
    noClick: true,
    accept: accept,
    onDrop: acceptedFiles => setFiles(prev => ([...prev, ...acceptedFiles].splice(0, maxFiles)))
  });

  useEffect(() => {
    if (isLoad) onChange(files);
    else setIsLoad(true);
    // eslint-disable-next-line
  }, [files]);

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className={classNames(styles.root, files && styles.filled, className)} {...getRootProps()}>
      <input {...getInputProps()} />
      {files.length ? (
        <div className={styles.files}>
          {files.map((file, index) => {
            const dotIndex = file?.name.lastIndexOf('.');
            const nameWithoutExt = file?.name.slice(0, dotIndex);
            const ext = file?.name.slice(dotIndex).toLowerCase();
            const size = file?.size || 0;

            return (
              <div className={styles.file} key={file.name}>
                <img
                  src={(ext && filesIcons[ext]) ? filesIcons[ext] : filesIcons.other}
                  className={styles.fileIcon}
                  alt={ext}
                />
                <div className={styles.fileName}>
                  {nameWithoutExt}
                </div>
                <span className={styles.fileSize}>
                  {size / 1024 >= 1024 ? `${(size / 1024 / 1024).toFixed(2)}MB` : `${Math.ceil(size / 1024)}Kb`}
                </span>
                <Button
                  className={styles.remove}
                  viewStyle="text"
                  iconClassName={styles.removeIcon}
                  icon={<ReactSVG src="/icons/trash.svg" wrapper="span" />}
                  onClick={() => handleRemoveFile(index)}
                  title={t('uikit:delete')}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className={styles.buttons}>
          <Button
            className={styles.button}
            viewStyle="secondary"
            size="small"
            onClick={e => {
              e.stopPropagation();
              open();
            }}
            icon={<ReactSVG src="/icons/upload.svg" wrapper="span" />}
            fullWidth
          >
            {buttonText ?? t('uikit:select_file')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FileInput;
